// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Get User Snippet
import { Client, GraphRequestOptions, PageCollection, PageIterator } from '@microsoft/microsoft-graph-client';
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';
import { Deleted, Event, User } from 'microsoft-graph';

let graphClient: Client | undefined = undefined;

function ensureClient(authProvider: AuthCodeMSALBrowserAuthenticationProvider) {
  if (!graphClient) {
    graphClient = Client.initWithMiddleware({
      authProvider: authProvider
    });
  }

  return graphClient;
}

export async function getUser(authProvider: AuthCodeMSALBrowserAuthenticationProvider): Promise<User> {
  ensureClient(authProvider);
  const user: User = await graphClient!.api('/me')
    .select('displayName,mail,mailboxSettings,userPrincipalName')
    .get();

  return user;
}

// Get User Week Calendar Snippet
export async function getUserWeekCalendar(authProvider: AuthCodeMSALBrowserAuthenticationProvider,
  timeZone: string, startDateTime: string, endDateTime: string): Promise<Event[]> {
  ensureClient(authProvider);

  var response: PageCollection = await graphClient!
    .api('/me/calendarview')
    .header('Prefer', `outlook.timezone="${timeZone}"`)
    .query({ startDateTime: startDateTime, endDateTime: endDateTime })
    .select('subject,organizer,start,end,attendees')
    .orderby('start/dateTime')
    .top(25)
    .get();

  if (response["@odata.nextLink"]) {
    var events: Event[] = [];
    var options: GraphRequestOptions = {
      headers: { 'Prefer': `outlook.timezone="${timeZone}"` }
    };

    var pageIterator = new PageIterator(graphClient!, response, (event) => {
      events.push(event);
      return true;
    }, options);

    await pageIterator.iterate();

    return events;
  } else {

    return response.value;
  }
}

// Create Event Snippet
export async function createEvent(authProvider: AuthCodeMSALBrowserAuthenticationProvider, newEvent: Event): Promise<Event> {
  ensureClient(authProvider);

  return await graphClient!
    .api('/me/events')
    .post(newEvent);
}

// Update Event Snippet
export async function updateEvent(authProvider: AuthCodeMSALBrowserAuthenticationProvider, event: Event, id: string): Promise<Event> {
  ensureClient(authProvider);

  return await graphClient!.api(`/me/events/${id}`).update(event);
}

// Delete Event Snippet
export async function deleteEvent(authProvider: AuthCodeMSALBrowserAuthenticationProvider, id: string): Promise<Deleted> {
  ensureClient(authProvider);

  return await graphClient!.api(`/me/events/${id}`).delete();
}